// Keeps the screen on while cooking, where the Screen Wake Lock API is
// supported; everywhere else it quietly does nothing.

interface Sentinel {
  release(): Promise<void>;
}

interface WakeLockNavigator {
  wakeLock?: { request(type: 'screen'): Promise<Sentinel> };
}

interface VisibilityDocument {
  readonly visibilityState: string;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface WakeLock {
  acquire(): Promise<void>;
  release(): Promise<void>;
}

export function createWakeLock(
  nav: WakeLockNavigator = navigator as WakeLockNavigator,
  doc: VisibilityDocument = document,
): WakeLock {
  let wanted = false;
  let sentinel: Sentinel | undefined;

  async function request() {
    if (!nav.wakeLock || sentinel || doc.visibilityState !== 'visible') {
      return;
    }
    try {
      sentinel = await nav.wakeLock.request('screen');
    } catch {
      // Refused (battery saver, permissions policy): cook with the screen
      // timing out as usual
    }
  }

  // The browser drops the lock when the tab is hidden; take it again when
  // the cook comes back. Only listening while the lock is wanted.
  function onVisibilityChange() {
    if (doc.visibilityState === 'visible') {
      void request();
    } else {
      sentinel = undefined;
    }
  }

  return {
    async acquire() {
      if (!wanted) {
        wanted = true;
        doc.addEventListener('visibilitychange', onVisibilityChange);
      }
      await request();
    },
    async release() {
      wanted = false;
      doc.removeEventListener('visibilitychange', onVisibilityChange);
      const held = sentinel;
      sentinel = undefined;
      await held?.release();
    },
  };
}
